import Dropzone from "@/components/molecules/Dropzone";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fileToBase64Raw } from "@/utils/FileUtils";
import { Grid } from "@mantine/core";
import CTA from "@/components/atoms/CTA";
import {
  UploadDocumentFormInput,
  UploadDocumentSchema,
} from "@/components/organisms/UploadFileModal/validation/validationSchema";
import { ArchiveDocument, FileType } from "@/types";
import { notifications } from "@mantine/notifications";
import { createDocumentForCompany } from "@/services/Documents";
import Select from "@/components/atoms/Select";
import DocumentTypeEnum from "@/constants/enums/DocumentTypeEnum";
import { enumToStringArray } from "@/utils/enumToArray";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface UploadFileModalProps {
  open: boolean;
  setOpen: any;
}

export const UploadFileModal: React.FC<UploadFileModalProps> = ({ open, setOpen }) => {
  const [files, setFiles] = useState<File[]>([]);
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();

  const { mutate: uploadFileMutation, isPending: isUploading } = useMutation({
    mutationFn: async (details: ArchiveDocument) => {
      return await createDocumentForCompany(details);
    },
    onSuccess: async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ["getDocumentArchiveByCompanyId", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.createSuccess"), t("notifications:document.uploadSuccess"))
      );
      reset();
      setFiles([]);
      setOpen(false);
    },
    onError: (_) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.createError"), t("notifications:document.uploadError"))
      );
    },
  });

  const { handleSubmit, reset, control } = useForm<UploadDocumentFormInput>({
    resolver: zodResolver(UploadDocumentSchema),
    values: {
      document: "",
      companyIds: [currentCompany?.companyId] as number[],
      fileType: "PDF",
      languageId: 1,
      documentType: DocumentTypeEnum.ATTACHMENT,
      documentTitle: "",
    },
  });

  const onSubmit = async (data: UploadDocumentFormInput) => {
    if (files.length === 0) {
      return;
    }
    const base64 = await fileToBase64Raw(files[0]);
    const translatedRequest = {
      ...data,
      document: base64,
      documentType: data.documentType as DocumentTypeEnum,
      fileType: files[0].type.split("/")[1].toUpperCase() as FileType,
      documentTitle: files[0].name,
    };
    uploadFileMutation(translatedRequest);
  };

  console.log("files", files);

  const documentTypes = enumToStringArray(DocumentTypeEnum);

  return (
    <Modal opened={open} onClose={setOpen} title={t("actions:uploadDocument")} size={"lg"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid p={20}>
          <Grid.Col span={12}>
            <Dropzone
              type={"dropzone"}
              multiple={false}
              showAsImageCarousel={false}
              origin={"local"}
              files={files}
              setFiles={setFiles}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="documentType"
              render={({ field, fieldState }) => {
                return (
                  <Select
                    {...field}
                    value={field.value?.toString()}
                    onChange={field.onChange}
                    id="documentType"
                    label={t("dashboard:documentType")}
                    data={documentTypes.map((option: string) => ({
                      value: option,
                      label: t(`enumerables:documentTypes.${option}`),
                    }))}
                    error={fieldState.error?.message}
                  />
                );
              }}
            />
          </Grid.Col>
        </Grid>
        <ModalCTAs>
          <CTA
            intent={"secondary"}
            type={"reset"}
            disabled={isUploading}
            onClick={() => {
              reset();
              setFiles([]);
              setOpen(false);
            }}
          >
            {t("actions:cancel")}
          </CTA>
          <CTA intent={"primary"} type={"submit"} disabled={isUploading} loading={isUploading}>
            {t("actions:uploadDocument")}
          </CTA>
        </ModalCTAs>
      </form>
    </Modal>
  );
};
